import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SalePaginationProps {
    total: number;
    limit: number;
    offset: number;
    onPageChange: (newOffset: number) => void;
    isLoading?: boolean;
}

export function SalePagination({
    total,
    limit,
    offset,
    onPageChange,
    isLoading
}: SalePaginationProps) {
    const currentPage = Math.floor(offset / limit) + 1;
    const totalPages = Math.ceil(total / limit);
    const start = offset + 1;
    const end = Math.min(offset + limit, total);
    const handleNext = () => {
        if (offset + limit >= total) {
            return;
        }
        if (onPageChange) {
            onPageChange(offset + limit)
        }
    }
    const handlePrev = () => {
        if (offset === 0) {
            return;
        }
        if (onPageChange) {
            onPageChange(offset - limit)
        }
    }

    return (
        <div className="flex items-center justify-between gap-2 px-2 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-t border-slate-200 shadow-sm rounded-b-lg">
            {/* Info Text - Ultra compact for mobile */}
            <div className="flex items-center gap-1 text-[10px] sm:text-xs">
                <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 bg-white rounded border border-blue-200">
                    <span className="font-bold text-blue-600">{total > 0 ? start : 0}</span>
                    <span className="text-slate-400">-</span>
                    <span className="font-bold text-blue-600">{end}</span>
                </div>
                <span className="text-slate-600">/</span>
                <span className="font-bold text-slate-900">{total}</span>
            </div>

            {/* Controls - Compact */}
            <div className="flex items-center gap-1.5 sm:gap-2">
                {/* Previous Button */}
                <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0 || isLoading}
                    onClick={handlePrev}
                    className="h-7 sm:h-8 w-7 sm:w-8 p-0 rounded-lg border-slate-300 bg-white hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="Previous page"
                >
                    <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>

                {/* Page Indicator - Compact */}
                <div className="px-1.5 sm:px-3 py-0.5 sm:py-1.5 bg-white rounded-lg border border-slate-300 shadow-sm">
                    <span className="text-[10px] sm:text-xs font-semibold text-slate-700 whitespace-nowrap">
                        <span className="text-blue-600">{currentPage}</span>
                        <span className="text-slate-400 mx-0.5">/</span>
                        <span className="text-slate-900">{totalPages || 1}</span>
                    </span>
                </div>

                {/* Next Button */}
                <Button
                    variant="outline"
                    size="sm"
                    disabled={offset + limit >= total || isLoading}
                    onClick={handleNext}
                    className="h-7 sm:h-8 w-7 sm:w-8 p-0 rounded-lg border-slate-300 bg-white hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="Next page"
                >
                    <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
            </div>
        </div>
    );
}
